import React, { useState, useRef } from "react";
import Button from "react-bootstrap/Button";
import Select from "react-select";
import { sendAuthenticatedAsyncRequest } from "services/AsyncRequestService";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import { Formik } from "formik";
import * as Yup from "yup";
import AdminPopupDismissableModal from "components/adminPopupDismissableModal";
import { getErrorMessage } from "services/UtilityService";
import { showNotification } from "services/NotificationService";
import { customMultiSelectStyles } from 'commons/styles/CustomStyles';

const RegisterNewUser = ({ showButton: initialShowButton, roles, handler }) => {
  const [showButton, setShowButton] = useState(initialShowButton);
  const modalRef = useRef(null);

  const registerUser = (values, setSubmitting, closeModalHandle) => {
    sendAuthenticatedAsyncRequest(
      `/user/register`,
      'POST',
      values,
      (r) => {
        setSubmitting(false);
        showNotification({
          title: 'Action successful!',
          message: 'User registered successfully.',
          position: 'tc',
          type: 'success',
        });
        closeModalHandle();
        handler();
      },
      (r) => {
        setSubmitting(false);
        showNotification({
          title: 'Error!',
          message: getErrorMessage(r.data) || getErrorMessage('UNABLE_TO_REGISTER_USER'),
          position: 'tc',
          type: 'error',
        });
      }
    );
  };

  const onMultiSelectChange = (options, setFieldValue) => {
    const tempRolesList = options.map((x) => x.key);
    setFieldValue('roles', tempRolesList);
  };

  const invertShowbutton = () => {
    setShowButton(!showButton);
  };

  const rolesOptions = roles
    ? roles.map((c) => ({
        value: c.roleDisplayName,
        key: c.name,
      }))
    : [];

  const registerUserFormSchema = Yup.object().shape({
    name: Yup.string()
      .max(60, "Maximum Length: " + 60)
      .required("Required"),
    email: Yup.string().email().required("Required"),
    roles: Yup.array().min(1, "Atleast select 1 role"),
    ipAddress: Yup.string().matches(
      /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/i,
      "Not a valid IP address"
    ),
  });

  if (showButton) {
    return (
      <Button
        onClick={invertShowbutton}
        size="sm"
        style={{
          float: "right",
          marginLeft: "1rem",
        }}
        variant="success"
        id="reset-state-btn-filter"
      >
        Add Users
      </Button>
    );
  }

  return (
    <>
      <Button
        onClick={invertShowbutton}
        size="sm"
        style={{
          float: "right",
          marginLeft: "1rem",
        }}
        variant="success"
        id="reset-state-btn-filter"
      >
        Add Users
      </Button>

      <AdminPopupDismissableModal
        ref={modalRef}
        openModalButtonText="Register User"
        openModalButtonStyle="info"
        openModalButtonBlock={true}
        openModalButtonDisabled={false}
        openModalButtonId="user-register-btn-popup"
        preventCloseWhenClickedOutsideModal={true}
        hideFooter={true}
        initialStateOpen={true}
        onClose={invertShowbutton}
        title="Register User"
      >
        <Formik
          initialValues={{ name: "", email: "", roles: [], ipAddress: "" }}
          onSubmit={(values, { setSubmitting }) => {
            registerUser(values, setSubmitting, () =>
              modalRef.current.handleClose()
            );
          }}
          validationSchema={registerUserFormSchema}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            handleSubmit,
            isSubmitting,
            setFieldValue,
          }) => (
            <Form onSubmit={handleSubmit}>
              <Row>
                <Col>
                  <Form.Group>
                    <Form.Label>Full Name</Form.Label>
                    <Form.Control
                      type="text"
                      value={values.name}
                      onChange={handleChange}
                      name="name"
                      onBlur={handleBlur}
                    ></Form.Control>
                    <Form.Text className="text-small text-danger">
                      {errors.name && touched.name && errors.name}
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col>
                  <Form.Group>
                    <Form.Label>Email</Form.Label>
                    <Form.Control
                      type="email"
                      value={values.email}
                      onChange={handleChange}
                      name="email"
                      onBlur={handleBlur}
                    ></Form.Control>
                    <Form.Text className="text-small text-danger">
                      {errors.email && touched.email && errors.email}
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col>
                  <Form.Group>
                    <Form.Label>Roles</Form.Label>
                    <Select
                      isMulti
                      placeholder="Select Role"
                      options={rolesOptions}
                      name="roles"
                      value={rolesOptions.filter((opt) =>
                        values.roles.includes(opt.key)
                      )}
                      onChange={(selected) =>
                        onMultiSelectChange(selected || [], setFieldValue)
                      }
                      getOptionLabel={(opt) => opt.value}
                      getOptionValue={(opt) => opt.key}
                      styles={{
                        ...customMultiSelectStyles,
                      }}
                    />
                    <Form.Text className="text-small text-danger">
                      {errors.roles && touched.roles && errors.roles}
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col>
                  <Form.Group>
                    <Form.Label>IP Address</Form.Label>
                    <Form.Control
                      type="text"
                      value={values.ipAddress}
                      onChange={handleChange}
                      name="ipAddress"
                      onBlur={handleBlur}
                    ></Form.Control>
                    <Form.Text className="text-small text-danger">
                      {errors.ipAddress &&
                        touched.ipAddress &&
                        errors.ipAddress}
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
              <Row className="bottom-spacer top-spacer">
                <Col md={{ offset: "4", span: "4" }}>
                  <Button
                    variant="outline-dark"
                    block
                    onClick={() => modalRef.current.handleClose()}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </Button>
                </Col>
                <Col md={4}>
                  <Button
                    type="submit"
                    variant="success"
                    block
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Registering... " : "Register"}
                  </Button>
                </Col>
              </Row>
            </Form>
          )}
        </Formik>
      </AdminPopupDismissableModal>
    </>
  );
};

export default RegisterNewUser;